import {
    Button,
    Card,
    CardActions,
    CardContent,
    FormControlLabel,
    Radio,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import React, { useCallback, useState } from 'react';

import { useHistory } from 'react-router';

import { CrisBrandPicker } from 'app/components';
import { EntityRadioField } from 'app/components/form-controls/entity-radio-field';
import { Translate } from 'app/components/translate';
import { BrandDeletionEnum } from 'app/enum';
import { Brand } from 'app/model/cris/brand';
import { messagingService } from 'app/services';
import { brandService } from 'app/services/brand.service';
import { dialogService } from 'app/services/dialog.service';
import { enumKeys } from 'app/utils';

interface BrandDeletionProps {
    brand: Brand;
}

const defaultValues: Record<string, boolean> = {};

enumKeys(BrandDeletionEnum).forEach((key) => {
    const value = BrandDeletionEnum[key];
    defaultValues[value] = true;
});

export const BrandDeletion: React.FC<BrandDeletionProps> = ({ brand }) => {
    const [selection, setSelection] = useState<Record<string, boolean>>(defaultValues);
    const [targetBrand, setTargetBrand] = useState<Brand>();
    const history = useHistory();

    const handleDeletion = useCallback(
        (deleteSelections: Record<string, boolean>) => {
            brandService
                .delete(brand, undefined, {
                    brand_delete_selection: {
                        targetBrand: targetBrand?.id,
                        destiny: deleteSelections,
                    },
                })
                .subscribe(() => {
                    messagingService.showSuccess('Brand Successfully Deleted');
                    history.replace(`../${targetBrand?.id ?? ''}/details`);
                });
        },
        [brand, history, targetBrand?.id],
    );

    return (
        <>
            <Typography variant='h3'>Brand Deletion - {brand.name}</Typography>
            <Typography variant='body1' gutterBottom>
                In order to delete the brand you must choose what should happen with all related records (ex. patterns)
            </Typography>

            <Card variant='outlined' className='mb-2'>
                <CardContent>
                    <Typography variant='h5'>1. Select Destination Brand</Typography>
                    <Typography variant='body1'>This is the brand all related items will be moved to.</Typography>
                    <CrisBrandPicker
                        label='Destination Brand'
                        size='small'
                        getOptionDisabled={(option) => brand.id === option.id}
                        value={targetBrand}
                        onChange={(evt, name, target) => setTargetBrand(target)}
                    />
                </CardContent>
            </Card>
            <Card variant='outlined' className='mb-2'>
                <CardContent>
                    <Typography variant='h5'>2. Select Destiny of Items</Typography>
                    <Typography variant='body1'>
                        Please, select what happens to each of items linked to this brand.
                    </Typography>
                    <Typography variant='body1'>
                        Currently stores cannot be Discarded in cascade manner. If you wish to discard stores, please go
                        to individual store page and use their individual Delete functionality to remove stores. Then
                        come back here to remove the brand.
                    </Typography>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Item</TableCell>
                                    <TableCell>Destiny</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {enumKeys(BrandDeletionEnum).map((enumKey) => (
                                    <TableRow key={enumKey}>
                                        <TableCell>
                                            <Translate tkey={`forms:BrandDeletionEnum.${enumKey}`} />
                                        </TableCell>
                                        <TableCell>
                                            <EntityRadioField
                                                name={BrandDeletionEnum[enumKey]}
                                                label=''
                                                value={selection}
                                                row
                                                onChange={(evt, name, value) => {
                                                    setSelection({ ...selection, [name]: 'true' === value });
                                                }}
                                            >
                                                <FormControlLabel value='true' control={<Radio />} label='Keep' />
                                                <FormControlLabel
                                                    disabled={
                                                        BrandDeletionEnum[enumKey] ===
                                                            BrandDeletionEnum.PARTNER_MAP_QUERIES ||
                                                        BrandDeletionEnum[enumKey] ===
                                                            BrandDeletionEnum.PARTNER_MAP_QUERY_MODS ||
                                                        BrandDeletionEnum[enumKey] === BrandDeletionEnum.STORES
                                                    }
                                                    value='false'
                                                    control={<Radio />}
                                                    label='Discard'
                                                />
                                            </EntityRadioField>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>

            <Card variant='outlined' className='mb-2'>
                <CardContent>
                    <Typography variant='h5'>3. Execute Deletion</Typography>
                    <Typography variant='body1'>There is no going back once you click this.</Typography>
                </CardContent>
                <CardActions>
                    <Button
                        type='button'
                        variant='contained'
                        color='error'
                        onClick={() => {
                            dialogService.showConfirm(
                                {
                                    message: `Proceed with deletion of brand "${brand.name}"?`,
                                },
                                () => handleDeletion(selection),
                            );
                        }}
                    >
                        Delete
                    </Button>
                </CardActions>
            </Card>
        </>
    );
};
